// Copyright 2025, Imprivata, Inc.  All rights reserved.

import type { AuthnRequest } from '@imprivata-cloud/authn';
import { ImprHeaders } from '@imprivata-cloud/common';
import type { DomSerialized } from '@imprivata-cloud/components';
import type { AppError } from '@imprivata-cloud/core';
import { base64ToJson, type CodingContext, jsonToBase64 } from '@imprivata-cloud/dataprivacy';
import { ClientHeader, defaultSystemData, Endpoints } from './constants';
import type {
  AuthenticatorVerifyRequest,
  EmailVerificationResponse,
  IdentityVerificationRecordRequest,
  IdProofingSessionStartRequest,
  IdProofingSessionStartResponse,
  IdProofingStatusGetRequest,
  IdProofingStatusGetResponse,
  IndividualAssuranceCancelRequest,
  IndividualAssuranceCompleteRequest,
  IndividualAssuranceStartRequest,
  IndividualAssuranceStartResponse,
  OtpSendByEmailRequest,
  OtpSendByEmailResponse,
  OtpSendBySmsRequest,
  OtpSendBySmsResponse,
  OtpVerifyRequest,
  SelfServiceLandingPageDataResponse,
  SupervisedAssuranceCancelRequest,
  SupervisedAssuranceCompleteRequest,
  SupervisedAssuranceStartRequest,
  SupervisedAssuranceStartResponse,
  UserConsentCheckRequest,
  UserConsentCheckResponse,
  UserConsentRevokeRequest,
  UserConsentSubmitRequest,
  UserPasswordChangeRequest,
  VerificationOtpRequest,
  VerificationOtpResponse,
} from './types';
import { FactorType } from './types';
import { createCodingCtxV2, fetcher } from './utils';

const getSelfServiceLandingPageData = async (): Promise<SelfServiceLandingPageDataResponse> => {
  return fetcher(Endpoints.SELF_SERVICE_V2_LANDING_PAGE_GET, { method: 'POST' });
};

const submitConsent = async (data: UserConsentSubmitRequest): Promise<void> => {
  return fetcher(Endpoints.CONSENT_SUBMIT, { method: 'POST', body: JSON.stringify(data) });
};

const revokeConsent = async (data: UserConsentRevokeRequest): Promise<void> => {
  return fetcher(Endpoints.CONSENT_REVOKE, { method: 'POST', body: JSON.stringify(data) });
};

const checkConsent = async (data: UserConsentCheckRequest): Promise<UserConsentCheckResponse> => {
  return fetcher(Endpoints.CONSENT_CHECK, { method: 'POST', body: JSON.stringify(data) });
};

// Get Legal Doc Content from json file
export async function getConsentLegalDocumentContent(url: string): Promise<DomSerialized> {
  return fetcher(url, { method: 'GET', credentials: 'omit' });
}

const enroll = async (authnData: AuthnRequest): Promise<AppError> => {
  const codingContext = await createCodingCtxV2();
  const codingCtxHeader = codingContext.buildImprCodingCtxHeader();

  const factorDataObj = authnData.factorData && base64ToJson(authnData.factorData);
  const systemDataObj = base64ToJson(authnData.systemData);

  return fetcher(Endpoints.SELF_SERVICE_V1_ENROLL, {
    method: 'POST',
    headers: { [ImprHeaders.ImprCodingCtx]: codingCtxHeader },
    body: JSON.stringify({
      ...authnData,
      factorData: factorDataObj && codingContext.encryptJson(factorDataObj),
      systemData: codingContext.encryptJson(systemDataObj),
    }),
  });
};

const changeImprIdpUserPassword = async (
  data: UserPasswordChangeRequest,
  codingContext?: CodingContext,
): Promise<void> => {
  return fetcher(Endpoints.SELF_SERVICE_CHANGE_PASSWORD, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: codingContext ? { 'Impr-Coding-Ctx': codingContext.buildImprCodingCtxHeader() } : {},
  });
};

const assurancePost = <T>(
  endpoint: string,
  body: unknown,
  workflowId: string,
  tenantId?: string,
  options?: { headers?: Record<string, string>; keepalive?: boolean },
): Promise<T> =>
  fetcher(
    endpoint,
    {
      method: 'POST',
      body: JSON.stringify(body),
      workflowId,
      keepalive: options?.keepalive,
      headers: {
        [ImprHeaders.ImprClientName]: jsonToBase64({ name: ClientHeader.IDP_WEB }),
        ...options?.headers,
      },
    },
    tenantId,
  );

// w/o tenantId the BE is unhappy and taking it from session is the only way for USS
export async function supervisedAssuranceCancel(
  data: SupervisedAssuranceCancelRequest,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  return assurancePost(Endpoints.USER_WEB_SUPERVISED_ASSURANCE_CANCEL, data, workflowId, tenantId, {
    keepalive: true,
  });
}

export async function supervisedAssuranceStart(
  data: SupervisedAssuranceStartRequest,
  workflowId: string,
  tenantId?: string,
): Promise<SupervisedAssuranceStartResponse> {
  return assurancePost(Endpoints.USER_WEB_SUPERVISED_ASSURANCE_START, data, workflowId, tenantId);
}

export async function supervisedAssuranceIdentityVerificationRecord(
  data: IdentityVerificationRecordRequest,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  return assurancePost(
    Endpoints.USER_WEB_SUPERVISED_ASSURANCE_IDENTITY_VERIFICATION_RECORD,
    data,
    workflowId,
    tenantId,
  );
}

export async function supervisedAssuranceAuthenticatorVerify(
  assuranceId: string,
  authenticatorId: string,
  tokenCode: string,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  const codingContext = await createCodingCtxV2();

  const data: AuthenticatorVerifyRequest = {
    assuranceId: assuranceId || '',
    authenticatorId: authenticatorId || '',
    factorType: FactorType.IMPRIVATA_ID,
    factorData: codingContext.encryptJson({ version: '1.0', totp: tokenCode }),
    systemData: codingContext.encryptJson(defaultSystemData),
  };

  return assurancePost(
    Endpoints.USER_WEB_SUPERVISED_ASSURANCE_AUTHENTICATOR_VERIFY,
    data,
    workflowId,
    tenantId,
    { headers: { [ImprHeaders.ImprCodingCtx]: codingContext.buildImprCodingCtxHeader() } },
  );
}

export async function supervisedAssuranceComplete(
  data: SupervisedAssuranceCompleteRequest,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  return assurancePost(
    Endpoints.USER_WEB_SUPERVISED_ASSURANCE_COMPLETE,
    data,
    workflowId,
    tenantId,
  );
}

export async function individualAssuranceStart(
  data: IndividualAssuranceStartRequest,
  workflowId: string,
  tenantId?: string,
): Promise<IndividualAssuranceStartResponse> {
  return assurancePost(Endpoints.USER_WEB_INDIVIDUAL_ASSURANCE_START, data, workflowId, tenantId);
}

// Clears the assurance record so a retry starts from a fresh assurance id. Fired on teardown, so
// keepalive, matching supervisedAssuranceCancel.
export async function individualAssuranceCancel(
  data: IndividualAssuranceCancelRequest,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  return assurancePost(Endpoints.USER_WEB_INDIVIDUAL_ASSURANCE_CANCEL, data, workflowId, tenantId, {
    keepalive: true,
  });
}

/** One factor bound to the authenticator being assured; factorData shapes come from authn's AuthnDataMap. */
export type IndividualAssuranceFactor =
  | { factorType: FactorType.PASSWORD; password: string }
  | { factorType: FactorType.IMPRIVATA_ID; authenticatorId: string; tokenCode: string };

export async function individualAssuranceAuthenticatorVerify(
  assuranceId: string,
  factor: IndividualAssuranceFactor,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  const codingContext = await createCodingCtxV2();

  const data: AuthenticatorVerifyRequest =
    factor.factorType === FactorType.IMPRIVATA_ID
      ? {
          assuranceId,
          factorType: FactorType.IMPRIVATA_ID,
          authenticatorId: factor.authenticatorId,
          factorData: codingContext.encryptJson({ version: '1.0', totp: factor.tokenCode }),
          systemData: codingContext.encryptJson(defaultSystemData),
        }
      : {
          assuranceId,
          factorType: FactorType.PASSWORD,
          factorData: codingContext.encryptJson({ version: '1.0', password: factor.password }),
        };

  return assurancePost(
    Endpoints.USER_WEB_INDIVIDUAL_ASSURANCE_AUTHENTICATOR_VERIFY,
    data,
    workflowId,
    tenantId,
    { headers: { [ImprHeaders.ImprCodingCtx]: codingContext.buildImprCodingCtxHeader() } },
  );
}

export async function individualAssuranceIdProofingSessionStart(
  data: IdProofingSessionStartRequest,
  workflowId: string,
  tenantId?: string,
): Promise<IdProofingSessionStartResponse> {
  return assurancePost(
    Endpoints.USER_WEB_INDIVIDUAL_ASSURANCE_ID_PROOFING_SESSION_START,
    data,
    workflowId,
    tenantId,
  );
}

export async function individualAssuranceIdProofingStatusGet(
  data: IdProofingStatusGetRequest,
  workflowId: string,
  tenantId?: string,
): Promise<IdProofingStatusGetResponse> {
  return assurancePost(
    Endpoints.USER_WEB_INDIVIDUAL_ASSURANCE_ID_PROOFING_STATUS_GET,
    data,
    workflowId,
    tenantId,
  );
}

export async function individualAssuranceSendOtpByEmail(
  data: OtpSendByEmailRequest,
  workflowId: string,
  tenantId?: string,
): Promise<OtpSendByEmailResponse> {
  return assurancePost(
    Endpoints.USER_WEB_INDIVIDUAL_ASSURANCE_OTP_SEND_BY_EMAIL,
    data,
    workflowId,
    tenantId,
  );
}

export async function individualAssuranceSendOtpBySms(
  data: OtpSendBySmsRequest,
  workflowId: string,
  tenantId?: string,
): Promise<OtpSendBySmsResponse> {
  return assurancePost(
    Endpoints.USER_WEB_INDIVIDUAL_ASSURANCE_OTP_SEND_BY_SMS,
    data,
    workflowId,
    tenantId,
  );
}

export async function individualAssuranceVerifyOtp(
  assuranceId: string,
  otpId: string,
  otp: string,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  const codingContext = await createCodingCtxV2();

  const data: OtpVerifyRequest = { assuranceId, otpId, otp: codingContext.encryptString(otp) };

  return assurancePost(
    Endpoints.USER_WEB_INDIVIDUAL_ASSURANCE_OTP_VERIFY,
    data,
    workflowId,
    tenantId,
    { headers: { [ImprHeaders.ImprCodingCtx]: codingContext.buildImprCodingCtxHeader() } },
  );
}

export async function individualAssuranceComplete(
  data: IndividualAssuranceCompleteRequest,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  return assurancePost(
    Endpoints.USER_WEB_INDIVIDUAL_ASSURANCE_COMPLETE,
    data,
    workflowId,
    tenantId,
  );
}

const changePasswordWithEncryption = async (newPassword: string): Promise<void> => {
  const codingContext = await createCodingCtxV2();

  const encryptedPassword = codingContext.encryptString(newPassword);

  return changeImprIdpUserPassword({ newPassword: encryptedPassword }, codingContext);
};

const confirmEmailVerification = async (
  data: VerificationOtpRequest,
  codingCtxHeader: string,
): Promise<VerificationOtpResponse> => {
  return fetcher(Endpoints.SELF_SERVICE_OTP_CONFIRM, {
    method: 'POST',
    headers: { [ImprHeaders.ImprCodingCtx]: codingCtxHeader },
    body: JSON.stringify(data),
  });
};

const requestEmailVerification = async (): Promise<EmailVerificationResponse> => {
  return fetcher(Endpoints.SELF_SERVICE_OTP_REQUEST, { method: 'POST' });
};

export {
  getSelfServiceLandingPageData,
  submitConsent,
  checkConsent,
  enroll,
  changeImprIdpUserPassword,
  revokeConsent,
  changePasswordWithEncryption,
  confirmEmailVerification,
  requestEmailVerification,
};
